import { instantMeiliSearch } from '@meilisearch/instant-meilisearch'
import { Meilisearch } from 'meilisearch'
import { useSearchParams } from 'next/navigation'
import { createContext, use, useMemo } from 'react'
import {
  MAIN_INDEX_ID,
  type MainIndex,
  type MainIndexRecord,
} from './etl/meilisearch/mainIndex'

export const MeilisearchApiKeyContext = createContext<string | undefined>(
  undefined,
)

export const EmbeddingModelContext = createContext<string | undefined>(
  undefined,
)

export function useSearchClient(): MainIndex | undefined {
  const apiKey = use(MeilisearchApiKeyContext)

  return useMemo(
    () =>
      process.env.NEXT_PUBLIC_MEILISEARCH_URL
        ? new Meilisearch({
            host: process.env.NEXT_PUBLIC_MEILISEARCH_URL,
            apiKey,
          }).index<MainIndexRecord>(MAIN_INDEX_ID)
        : undefined,
    [apiKey],
  )
}

export function useInstantSearchClient({
  placeholderSearch = false,
}: {
  placeholderSearch?: boolean
} = {}) {
  const searchParams = useSearchParams()
  const embeddingModel = use(EmbeddingModelContext)
  const hybrid = searchParams.get('hybrid') === 'true'
  const apiKey = use(MeilisearchApiKeyContext)

  const { searchClient } = useMemo(() => {
    if (!process.env.NEXT_PUBLIC_MEILISEARCH_URL) {
      throw new Error('NEXT_PUBLIC_MEILISEARCH_URL is not defined')
    }
    const { searchClient, setMeiliSearchParams, meiliSearchInstance } =
      instantMeiliSearch(process.env.NEXT_PUBLIC_MEILISEARCH_URL, apiKey, {
        placeholderSearch,
      })

    if (hybrid && embeddingModel) {
      setMeiliSearchParams({
        hybrid: {
          embedder: embeddingModel,
          semanticRatio: 0.5,
        },
      })
    } else {
      setMeiliSearchParams({
        hybrid: undefined,
      })
    }

    return {
      searchClient,
      setMeiliSearchParams,
      meiliSearchInstance,
    }
  }, [apiKey, hybrid, embeddingModel, placeholderSearch])

  return searchClient
}
